<template>
  <div class="row g-3">
    <div class="col-12">
      <div class="form-check form-switch form-check-custom">
        <input class="form-check-input" type="checkbox"
               :id="idPrefix + '_compounded'"
               :checked="compounded"
               @change="setCompounded($event.target.checked)">
        <label class="form-check-label ms-2" :for="idPrefix + '_compounded'">Fórmula manipulada</label>
      </div>
    </div>

    <template v-if="compounded">
      <div class="col-12">
        <label :for="idPrefix + '_drug_name'" class="form-label required">Nome da fórmula</label>
        <input class="form-control form-control-solid" :id="idPrefix + '_drug_name'"
               v-model="drug.drug_name" maxlength="500" placeholder="Dê um nome a sua fórmula">
        <small class="text-muted">Esse nome fica no modelo, para você achar a fórmula. Ele não sai na receita.</small>
        <small class="text-danger d-block" v-if="errors.drug_name">{{ errors.drug_name[0] }}</small>
      </div>

      <div class="col-md-6">
        <label :for="idPrefix + '_regulatory'" class="form-label required">Tipo de receituário</label>
        <SelectUbbi v-if="agendaStyle" :id="idPrefix + '_regulatory'"
                    v-model="drug.regulatory_type" :options="regulatoryTypes"
                    option-label="name" option-value="code" placeholder="Selecione uma opção"/>
        <select v-else class="form-control form-select" :id="idPrefix + '_regulatory'" v-model="drug.regulatory_type">
          <option value="" disabled>Selecione uma opção</option>
          <option :value="type.code" v-for="type in regulatoryTypes" :key="type.code">{{ type.name }}</option>
        </select>
        <small class="text-danger" v-if="errors.regulatory_type">{{ errors.regulatory_type[0] }}</small>
      </div>
      <div class="col-md-6">
        <label :for="idPrefix + '_form'" class="form-label required">Forma física</label>
        <SelectUbbi v-if="agendaStyle" :id="idPrefix + '_form'"
                    v-model="drug.pharmaceutical_form" :options="physicalForms"
                    option-label="label" option-value="value" placeholder="Ex: Gel creme"/>
        <select v-else class="form-control form-select" :id="idPrefix + '_form'" v-model="drug.pharmaceutical_form">
          <option value="" disabled>Ex: Gel creme</option>
          <option :value="form.value" v-for="form in physicalForms" :key="form.value">{{ form.label }}</option>
        </select>
        <small class="text-danger" v-if="errors.pharmaceutical_form">{{ errors.pharmaceutical_form[0] }}</small>
      </div>

      <div class="col-md-6">
        <label :for="idPrefix + '_quantity'" class="form-label required">Quantidade</label>
        <input class="form-control form-control-solid" :id="idPrefix + '_quantity'"
               v-model="drug.quantity" maxlength="255" placeholder="Ex: 100">
        <small class="text-danger" v-if="errors.quantity">{{ errors.quantity[0] }}</small>
      </div>
      <div class="col-md-6">
        <label :for="idPrefix + '_measure'" class="form-label required">Medida</label>
        <SelectUbbi v-if="agendaStyle" :id="idPrefix + '_measure'"
                    v-model="drug.measure" :options="measures"
                    option-label="label" option-value="value" placeholder="Ex: mL"/>
        <select v-else class="form-control form-select" :id="idPrefix + '_measure'" v-model="drug.measure">
          <option value="" disabled>Ex: mL</option>
          <option :value="measure.value" v-for="measure in measures" :key="measure.value">{{ measure.label }}</option>
        </select>
        <small class="text-danger" v-if="errors.measure">{{ errors.measure[0] }}</small>
      </div>

      <div class="col-12">
        <label :for="idPrefix + '_composition'" class="form-label required">Composição</label>
        <textarea class="form-control form-control-solid" rows="5" :id="idPrefix + '_composition'"
                  v-model="drug.composition" maxlength="5000"
                  placeholder="Digite ou cole aqui sua fórmula, um componente por linha"></textarea>
        <small class="text-danger" v-if="errors.composition">{{ errors.composition[0] }}</small>
      </div>
      <div class="col-12">
        <label :for="idPrefix + '_dosage'" class="form-label required">Posologia</label>
        <textarea class="form-control form-control-solid" rows="3" :id="idPrefix + '_dosage'"
                  v-model="drug.dosage_desc" maxlength="500"
                  placeholder="Digite ou cole aqui sua posologia"></textarea>
        <small class="text-danger" v-if="errors.dosage_desc">{{ errors.dosage_desc[0] }}</small>
      </div>
    </template>

    <template v-else>
      <div class="col-md-9">
        <label :for="idPrefix + '_drug_name'" class="form-label required">Medicamento</label>
        <InputSearchDrug :id="idPrefix + '_drug_name'"
                         :model-value="drug.drug_name"
                         @update:modelValue="drug.drug_name = $event"
                         @onSetSearchDrug="$emit('catalog', $event)"
                         @onClearSearchDrug="$emit('clear-catalog')"
        />
        <small class="text-danger" v-if="errors.drug_name">{{ errors.drug_name[0] }}</small>
      </div>
      <div class="col-md-3">
        <label :for="idPrefix + '_quantity'" class="form-label" :class="{ required: drug.controlled }">Quantidade</label>
        <input class="form-control form-control-solid" :id="idPrefix + '_quantity'"
               v-model="drug.quantity" maxlength="255" placeholder="Ex.: 30 cp">
      </div>

      <div class="col-md-4">
        <label :for="idPrefix + '_form'" class="form-label">Forma farmacêutica</label>
        <input class="form-control form-control-solid" :id="idPrefix + '_form'"
               v-model="drug.pharmaceutical_form" maxlength="255" placeholder="Ex.: COMPRIMIDO">
      </div>
      <div class="col-md-3">
        <label :for="idPrefix + '_concentration'" class="form-label">Concentração</label>
        <input class="form-control form-control-solid" :id="idPrefix + '_concentration'"
               v-model="drug.concentration" maxlength="255" placeholder="Ex.: 500 MG">
      </div>
      <div class="col-md-5">
        <label :for="idPrefix + '_admin'" class="form-label required">Via de administração</label>
        <SelectUbbi v-if="agendaStyle" :id="idPrefix + '_admin'"
                    v-model="drug.id_drug_admin" :options="drugAdmins"
                    option-label="drug_admin_name" option-value="id_drug_admin" />
        <select v-else class="form-control form-select" :id="idPrefix + '_admin'"
                v-model="drug.id_drug_admin">
          <option value="" disabled>--Selecione--</option>
          <option :value="drugAdmin.id_drug_admin" v-for="drugAdmin in drugAdmins" :key="drugAdmin.id_drug_admin">
            {{ drugAdmin.drug_admin_name }}
          </option>
        </select>
        <small class="text-danger" v-if="errors.id_drug_admin">{{ errors.id_drug_admin[0] }}</small>
      </div>

      <div class="col-md-8">
        <div class="drug-form-label-row">
          <label :for="idPrefix + '_dosage'" class="form-label required mb-0">Posologia</label>
          <div class="form-check form-check-custom">
            <input class="form-check-input" type="checkbox" :id="idPrefix + '_continuous'"
                   v-model="drug.continuous_use">
            <label class="form-check-label ms-2" :for="idPrefix + '_continuous'">Uso contínuo</label>
          </div>
        </div>
        <input class="form-control form-control-solid"
               v-model="drug.dosage_desc" :id="idPrefix + '_dosage'"
               placeholder="Ex.: 1 comprimido de 8 em 8 horas">
        <small class="text-danger" v-if="errors.dosage_desc">{{ errors.dosage_desc[0] }}</small>
      </div>
      <div class="col-md-4">
        <div class="drug-form-label-row">
          <label :for="idPrefix + '_treatment'" class="form-label mb-0">
            {{ drug.continuous_use ? 'Quantidade a cada 30 dias' : 'Tempo de tratamento' }}
          </label>
        </div>
        <input v-if="drug.continuous_use" class="form-control form-control-solid"
               :id="idPrefix + '_monthly'" v-model="drug.monthly_quantity"
               maxlength="255" placeholder="Ex.: 30 comprimidos">
        <input v-else class="form-control form-control-solid" :id="idPrefix + '_treatment'"
               v-model="drug.treatment_time" maxlength="255" placeholder="Ex.: 7 dias">
        <small class="text-danger" v-if="errors.monthly_quantity">{{ errors.monthly_quantity[0] }}</small>
        <small v-else-if="drug.continuous_use" class="form-text">
          Exigida pelo art. 9º da RDC 471/2021 para a receita valer 90 dias.
        </small>
      </div>

      <div class="col-12">
        <label :for="idPrefix + '_obs'" class="form-label">Observação</label>
        <input class="form-control form-control-solid" :id="idPrefix + '_obs'"
               v-model="drug.drug_obs" maxlength="500" placeholder="Opcional">
      </div>
    </template>
  </div>
</template>

<script>
import SelectUbbi from "@/components/SelectUbbi.vue";
import InputSearchDrug from "@/components/InputSearchDrug.vue";
import {loadRegulatoryTypes, regulatoryTypes} from "@/helpers/regulatoryTypes";

const PHYSICAL_FORMS = [
  'Cápsula', 'Creme', 'Gel', 'Gel creme', 'Loção', 'Pomada', 'Solução',
  'Xarope', 'Shampoo', 'Sachê', 'Óvulo', 'Supositório', 'Spray', 'Pasta',
];

const MEASURES = ['mL', 'g', 'mg', 'un', 'cápsula', 'dose', 'embalagem', 'flaconete'];

export default {
  name: "PrescriptionDrugFields",
  components: {SelectUbbi, InputSearchDrug},
  emits: ['catalog', 'clear-catalog'],
  props: {
    agendaStyle: Boolean,
    drug: {
      type: Object,
      required: true,
    },
    drugAdmins: {
      type: Array,
      default: () => [],
    },
    errors: {
      type: Object,
      default: () => ({}),
    },
    idPrefix: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      regulatoryTypes: regulatoryTypes(),
    };
  },
  computed: {
    compounded() {
      return this.drug.item_kind === 'compounded';
    },
    physicalForms() {
      return PHYSICAL_FORMS.map((form) => ({value: form, label: form}));
    },
    measures() {
      return MEASURES.map((measure) => ({value: measure, label: measure}));
    },
  },
  mounted() {
    loadRegulatoryTypes().then((types) => {
      this.regulatoryTypes = types;
    }).catch(() => {});
  },
  watch: {
    'drug.continuous_use'(continuo) {
      if (this.compounded) {
        return;
      }
      if (continuo) {
        this.drug.treatment_time = '';
        return;
      }
      this.drug.monthly_quantity = '';
    },
  },
  methods: {
    setCompounded(on) {
      this.drug.item_kind = on ? 'compounded' : 'medication';
      if (!on) {
        this.drug.composition = '';
        this.drug.measure = '';
        return;
      }

      this.drug.id_drug = null;
      this.drug.concentration = '';
      this.drug.continuous_use = false;
      this.drug.monthly_quantity = '';
      this.drug.treatment_time = '';
      this.drug.drug_obs = '';
      this.drug.id_drug_admin = '';
      this.drug.regulatory_type = this.drug.regulatory_type || 'SIMPLES';
      this.$emit('clear-catalog');
    },
  },
};
</script>

<style scoped>
.drug-form-label-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  min-height: 28px;
  margin-bottom: 0.35rem;
}
</style>
